import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, ChevronDown, Shield, GraduationCap, TrendingUp, Sun, Moon } from 'lucide-react';
import { Logo } from './Logo';
import { useDarkMode } from '../hooks/useDarkMode';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './NotificationBell';

const aiToolLinks = [
  { href: '/tools/credentials', label: 'Credential Tracker', icon: Shield, desc: 'Track licenses & compliance' },
  { href: '/tools/interview-prep', label: 'Interview Prep', icon: GraduationCap, desc: 'AI-powered coaching' },
  { href: '/tools/demand-forecast', label: 'Demand Forecast', icon: TrendingUp, desc: 'Market intelligence' },
];

const navLinks = [
  { href: '#staffing', label: 'Who We Place', type: 'scroll' },
  { href: '/about', label: 'About Us', type: 'page' },
  { href: '#services', label: 'Services', type: 'scroll' },
  { href: '/careers', label: 'Careers', type: 'page' },
  { href: '#why-us', label: 'Why Us', type: 'scroll' },
  { href: '#contact', label: 'Contact', type: 'scroll' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if a nav link is active based on current path
  const isActiveLink = (link) => {
    if (link.type === 'page') {
      return location.pathname === link.href;
    }
    return false;
  };

  const handleNavClick = (link) => {
    setIsOpen(false);
    
    if (link.type === 'page') {
      navigate(link.href);
    } else {
      // If we're not on homepage, navigate there first
      if (location.pathname !== '/') {
        navigate('/' + link.href);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get nav link classes with active state
  const getNavLinkClasses = (link) => {
    const baseClasses = "nav-link transition-colors";
    if (isActiveLink(link)) {
      return `${baseClasses} text-primary font-semibold`;
    }
    return baseClasses;
  };

  // Get mobile nav link classes with active state
  const getMobileNavLinkClasses = (link) => {
    const baseClasses = "text-left text-lg font-medium transition-colors py-2";
    if (isActiveLink(link)) {
      return `${baseClasses} text-primary`;
    }
    return `${baseClasses} text-foreground hover:text-primary`;
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 h-20 transition-all duration-300 ${
        isScrolled || location.pathname !== '/' ? 'glass-header shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="container-width h-full flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          onClick={handleLogoClick}
          data-testid="logo-link"
          className="flex items-center"
        >
          <Logo />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            link.type === 'page' ? (
              <Link
                key={link.href}
                to={link.href}
                className={getNavLinkClasses(link)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={getNavLinkClasses(link)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </button>
            )
          ))}
          {/* AI Tools Dropdown */}
          <div className="relative" ref={toolsRef}>
            <button
              onClick={() => setToolsOpen(!toolsOpen)}
              className={`nav-link transition-colors flex items-center gap-1 ${aiToolLinks.some(l => location.pathname === l.href) ? 'text-primary font-semibold' : ''}`}
              data-testid="nav-ai-tools"
            >
              AI Tools <ChevronDown className={`h-3.5 w-3.5 transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
            </button>
            {toolsOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-xl shadow-lg border border-border/50 py-2 z-50" data-testid="ai-tools-dropdown">
                {aiToolLinks.map((tool) => (
                  <Link
                    key={tool.href}
                    to={tool.href}
                    onClick={() => setToolsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors ${location.pathname === tool.href ? 'bg-secondary text-primary' : 'text-foreground'}`}
                    data-testid={`nav-tool-${tool.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <tool.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{tool.label}</p>
                      <p className="text-xs text-muted-foreground">{tool.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* CTA Button + Dark Mode Toggle */}
        <div className="hidden lg:flex items-center gap-2">
          <NotificationBell />
          <button
            onClick={toggleDarkMode}
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors"
            data-testid="dark-mode-toggle"
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          {user ? (
            <Link to="/tools/credentials">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary cursor-pointer hover:bg-primary/20 transition-colors" data-testid="user-avatar">
                {user.name?.[0]?.toUpperCase() || '?'}
              </div>
            </Link>
          ) : (
            <Link to="/apply-now">
              <Button className="bg-primary hover:bg-primary/90 text-white btn-hover-lift rounded-full px-6" data-testid="apply-now-btn">
                Apply Now
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:w-80">
            <div className="flex flex-col gap-6 mt-8">
              <Logo />
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  link.type === 'page' ? (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={getMobileNavLinkClasses(link)}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <button
                      key={link.href}
                      onClick={() => handleNavClick(link)}
                      className={getMobileNavLinkClasses(link)}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.label}
                    </button>
                  )
                ))}
              </nav>
              {/* Mobile AI Tools */}
              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">AI Tools</p>
                {aiToolLinks.map((tool) => (
                  <Link
                    key={tool.href}
                    to={tool.href}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 py-2.5 ${location.pathname === tool.href ? 'text-primary' : 'text-foreground hover:text-primary'}`}
                    data-testid={`mobile-nav-tool-${tool.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <tool.icon className="h-4 w-4" />
                    <span className="text-base font-medium">{tool.label}</span>
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <Link to="/apply-now" onClick={() => setIsOpen(false)} className="flex-1">
                  <Button
                    className="bg-primary hover:bg-primary/90 text-white rounded-full w-full"
                    data-testid="mobile-apply-now-btn"
                  >
                    Apply Now
                  </Button>
                </Link>
                <button
                  onClick={toggleDarkMode}
                  className="w-11 h-11 rounded-full flex items-center justify-center border border-border text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors shrink-0"
                  data-testid="mobile-dark-mode-toggle"
                  aria-label="Toggle dark mode"
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
